export type { SIPPlanAdviceCardDisplay } from '../../types/adviceCards.types'
import type {
  ElitePrimeAllocationSipPlanData,
  ElitePrimeStrategyData
} from '../../../../types/elitePrimeSummary.types'
import type { AdviceCardStat, SIPPlanAdviceCardDisplay } from '../../types/adviceCards.types'
import {
  buildStrategyFindCopyDisplay,
  buildStrategyUnderReviewStats,
  isStrategyFindCopyStatus,
  isStrategyQuestionnaireSubmittedStatus
} from './adviceCardStrategyStatusDisplay'

function formatSipCountValue(count: number): string {
  if (count === 1) {
    return '1 SIP'
  }
  return `${count} SIPs`
}

function buildActiveSipPlanStats(planData: ElitePrimeAllocationSipPlanData): AdviceCardStat[] {
  const stats: AdviceCardStat[] = []
  const sipsToStart = planData.startSipCount ?? 0
  const sipsToStop = planData.pauseSipCount ?? 0
  const sipFrom = planData.sipFrom ?? 0
  const sipTo = planData.sipTo ?? 0

  if (sipsToStart > 0) {
    stats.push({ value: formatSipCountValue(sipsToStart), label: 'to start' })
  }

  if (sipsToStop > 0) {
    stats.push({
      value: sipsToStop === 1 ? '1 SIP' : `${sipsToStop} SIP`,
      label: 'to stop'
    })
  }

  if (sipFrom > 0 && sipTo > 0 && sipFrom !== sipTo) {
    stats.push({
      value: `${formatSipCountValue(sipFrom)} → ${formatSipCountValue(sipTo)}`,
      label: sipFrom > sipTo ? 'to consolidate' : 'to diversify'
    })
  }

  return stats
}

function buildEmptySipPlanStats(): AdviceCardStat[] {
  return [{ value: 'No SIPs', label: 'to update' }]
}

export function buildSIPPlanAdviceCardDisplay({
  planData,
  strategyData
}: {
  planData?: ElitePrimeAllocationSipPlanData
  strategyData?: ElitePrimeStrategyData
}): SIPPlanAdviceCardDisplay {
  const status = strategyData?.status

  if (isStrategyFindCopyStatus(status)) {
    return buildStrategyFindCopyDisplay('Find your allocation strategy for SIP plan')
  }

  if (isStrategyQuestionnaireSubmittedStatus(status)) {
    return {
      stats: buildStrategyUnderReviewStats({
        planLabel: 'SIP Plan',
        planAvailableAt: planData?.planAvailableAt,
        planAvailableInDays: strategyData?.planAvailableInDays
      }),
      insightTone: 'orange'
    }
  }

  if (!planData) {
    return { stats: [], insightTone: 'orange' }
  }

  const planAvailableAt = planData.planAvailableAt
  if ((planAvailableAt ?? 0) > Date.now()) {
    return {
      stats: buildStrategyUnderReviewStats({
        planLabel: 'SIP Plan',
        planAvailableAt
      }),
      insightTone: 'orange'
    }
  }

  const activeStats = buildActiveSipPlanStats(planData)
  if (activeStats.length > 0) {
    return { stats: activeStats, insightTone: 'orange' }
  }

  return { stats: buildEmptySipPlanStats(), insightTone: 'orange' }
}
